// =============================================================================
// features/conversations/components/CommercialSection.tsx — Seção "Comercial"
// no ContactPanel do live chat (F38-S04, doc
// planejamento-livechat-contexto-comercial.md §2.2/§2.2.1).
//
// O atendente vê e AGE sobre o mundo comercial do lead sem sair da
// conversa: retorno, campanha, tentativas, restrições. ZERO formulário
// novo — todo bloco reusa componentes existentes de `features/sales`
// (`AppointmentDrawer`, `ScheduleAppointmentDialog`, `RegisterContactDialog`,
// `BlockContactDialog`, `ContactAttemptsHistory`, `ContactRestrictionBanner`,
// `CampaignMembershipCard`, o MESMO card montado pelo CRM no F38-S06).
//
// Gate em duas camadas (D1/D3 do plano):
//   - Seção inteira: `leadId != null` + flag de composição
//     `livechat.commercial_context.enabled` + permissão
//     `livechat:conversation:read` (mesmo piso do endpoint agregador e da
//     `QualificationRuler`, F37-S05) — sem os três, a seção NÃO existe no
//     DOM (nenhum nó extra, painel idêntico ao de hoje).
//   - Cada bloco interno: flag + permissão PRÓPRIA (tabela §2.2 do plano) —
//     sem o par, o bloco específico some, os demais continuam.
//
// Dados: `useCommercialContext(conversationId)` (features/conversations/queries.ts)
// — GET /api/conversations/:id/commercial-context, agregador único (F38-S01)
// reusado pelo CRM via `useLeadCommercialContext(leadId)`. Key ISOLADA
// (`conversationKeys.commercialContext`) — nenhuma key existente é tocada.
//
// Registrar contato/desfecho aqui NÃO passa `onResolveQueueMember`: o
// casamento com a campanha é server-side desde F38-S03 (`POST
// /contact-attempts/:id/outcome` resolve o membro ativo na mesma
// transação) — este componente só invalida a query após cada mutação.
//
// LGPD (doc 17 §8.3/§8.5): `reason`/`notes`/`nextStep` (texto livre) só
// chegam via os componentes reusados, que já tratam exibição — nada é
// logado aqui.
//
// DS (doc 18): tokens de cor/espaçamento/elevação do painel — mesmo padrão
// visual de `LeadSection`/`QualificationRuler` no `ContactPanel` (blocos com
// `padding:16px` + `border-bottom`), envolvendo os componentes de
// `features/sales` (que têm seu próprio estilo Tailwind — reuso ganha sobre
// uniformidade visual estrita, DoD explícito do slot: "zero formulário
// novo").
// =============================================================================

import type { Appointment, ContactChannel } from '@elemento/shared-schemas';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import { formatDateTimeOrgTz } from '../../../hooks/sales/orgTimezone';
import { useAppointments } from '../../../hooks/sales/useAppointments';
import { useContactPreferences } from '../../../hooks/sales/useContactPreferences';
import { useCampaignMembershipGate } from '../../../hooks/sales/useLeadCommercialContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import {
  AppointmentDrawer,
  BlockContactDialog,
  CampaignMembershipCard,
  ContactAttemptsHistory,
  ContactRestrictionBanner,
  describeAttempt,
  RegisterContactDialog,
  ScheduleAppointmentDialog,
} from '../../sales';
// Rótulos de canal — mesma fonte que os diálogos reusados usam (import
// direto do arquivo: `features/sales/**` é só-leitura neste slot, sem
// exportação própria no barrel para este mapa interno).
import { CHANNEL_LABELS } from '../../sales/contacts/labels';
import { conversationKeys, useCommercialContext } from '../queries';

// ─── Gate — próximo retorno / tentativas / restrições ────────────────────────
// Mesma dupla flag+permissão de `ScheduleReturnButton.tsx` e
// `hooks/crm/useLeadAppointments.ts` (useAppointmentsGate) — cópia local
// deliberada: `hooks/crm/**` está fora de `files_allowed` deste slot, e
// duplicar 4 constantes é preferível a importar de uma árvore de outro dono
// (mesma decisão documentada no header de `hooks/sales/orgTimezone.ts`).
const APPOINTMENTS_FLAG_KEY = 'sales.appointments.enabled';
const APPOINTMENTS_READ_PERMISSION = 'sales_appointments:read';
const APPOINTMENTS_WRITE_PERMISSION = 'sales_appointments:write';

// Gate da seção inteira — composição (D1 do plano) + piso de leitura da
// conversa, mesmo par de `QualificationRuler.tsx` (F37-S05).
const SECTION_FLAG_KEY = 'livechat.commercial_context.enabled';
const SECTION_READ_PERMISSION = 'livechat:conversation:read';

interface AppointmentsGate {
  canRead: boolean;
  canWrite: boolean;
}

function useAppointmentsGateLocal(): AppointmentsGate {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled } = useFeatureFlag(APPOINTMENTS_FLAG_KEY);
  const canRead = hasPermission(APPOINTMENTS_READ_PERMISSION) && flagEnabled;
  const canWrite = canRead && hasPermission(APPOINTMENTS_WRITE_PERMISSION);
  return { canRead, canWrite };
}

// ─── Primitivos visuais locais (mesmo padrão de ContactPanel.tsx) ────────────

function BlockHeader({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--text-3)',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

function Block({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {children}
    </div>
  );
}

// ─── Bloco: Próximo retorno ───────────────────────────────────────────────────

function NextAppointmentBlock({
  leadId,
  leadName,
  nextAppointment,
  isLoading,
  gate,
  isFullyBlocked,
  onChanged,
}: {
  leadId: string;
  leadName: string | null;
  nextAppointment: {
    id: string;
    scheduledAt: string;
    channel: ContactChannel;
    assignedAgentName: string;
  } | null;
  isLoading: boolean;
  gate: AppointmentsGate;
  isFullyBlocked: boolean;
  onChanged: () => void;
}): React.JSX.Element | null {
  const [scheduleOpen, setScheduleOpen] = React.useState(false);
  const [drawerAppointmentId, setDrawerAppointmentId] = React.useState<string | null>(null);

  // Full `Appointment` para o AppointmentDrawer — o agregador só devolve um
  // resumo (id/scheduledAt/channel/assignedAgentName), insuficiente para os
  // 3 painéis do drawer (concluir/reagendar/cancelar). Busca só quando o
  // drawer abre (enabled), pelo MESMO endpoint que a Agenda já usa.
  const { data: appointmentsData } = useAppointments(
    { leadId: [leadId], status: ['scheduled'], limit: 20 },
    { enabled: drawerAppointmentId !== null },
  );
  const drawerAppointment: Appointment | null =
    appointmentsData?.data.find((a) => a.id === drawerAppointmentId) ?? null;

  if (!gate.canRead) return null;

  return (
    <Block>
      <BlockHeader>Próximo retorno</BlockHeader>

      {isLoading ? (
        <div
          style={{
            height: 32,
            borderRadius: 6,
            background: 'var(--surface-muted)',
          }}
        />
      ) : nextAppointment ? (
        <button
          type="button"
          onClick={() => setDrawerAppointmentId(nextAppointment.id)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            textAlign: 'left',
            background: 'var(--bg-inset)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            padding: '8px 10px',
            cursor: 'pointer',
            transition: `all var(--dur-fast) var(--ease)`,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            {formatDateTimeOrgTz(nextAppointment.scheduledAt)}
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-3)' }}>
            {nextAppointment.assignedAgentName} · {CHANNEL_LABELS[nextAppointment.channel]}
          </span>
        </button>
      ) : gate.canWrite ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setScheduleOpen(true)}
          disabled={isFullyBlocked}
          title={isFullyBlocked ? 'Contato bloqueado — veja o aviso acima' : undefined}
        >
          Agendar retorno
        </Button>
      ) : (
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-3)',
            margin: 0,
            fontStyle: 'italic',
          }}
        >
          Sem retorno agendado.
        </p>
      )}

      {scheduleOpen && (
        <ScheduleAppointmentDialog
          leadId={leadId}
          {...(leadName ? { leadName } : {})}
          onClose={() => setScheduleOpen(false)}
          onScheduled={() => {
            setScheduleOpen(false);
            onChanged();
          }}
        />
      )}

      {drawerAppointment && (
        <AppointmentDrawer
          appointment={drawerAppointment}
          now={new Date()}
          onClose={() => {
            setDrawerAppointmentId(null);
            onChanged();
          }}
        />
      )}
    </Block>
  );
}

// ─── Bloco: Últimas tentativas (preview + colapsável "ver todas") ────────────

interface AttemptPreview extends ReturnType<typeof describeAttempt> {
  readonly id: string;
}

function AttemptsBlock({
  leadId,
  gate,
  recentAttempts,
}: {
  leadId: string;
  gate: AppointmentsGate;
  recentAttempts: readonly AttemptPreview[];
}): React.JSX.Element | null {
  const [expanded, setExpanded] = React.useState(false);

  if (!gate.canRead) return null;
  if (recentAttempts.length === 0 && !expanded) return null;

  return (
    <Block>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <BlockHeader>Últimas tentativas</BlockHeader>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--brand-azul)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {expanded ? 'Ocultar' : 'Ver todas'}
        </button>
      </div>

      {!expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {recentAttempts.map((preview) => (
            <div
              key={preview.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--text)' }}>
                {preview.channelLabel} · {preview.originLabel}
              </span>
              <span
                style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-3)' }}
              >
                {preview.isPending ? 'Pendente' : (preview.outcomeLabel ?? '—')}
              </span>
            </div>
          ))}
        </div>
      )}

      {expanded && <ContactAttemptsHistory leadId={leadId} enabled={gate.canRead} />}
    </Block>
  );
}

// ─── Bloco: Restrições ────────────────────────────────────────────────────────

function RestrictionsBlock({
  leadId,
  leadName,
  gate,
}: {
  leadId: string;
  leadName: string | null;
  gate: AppointmentsGate;
}): React.JSX.Element | null {
  const [blockOpen, setBlockOpen] = React.useState(false);
  const { toast } = useToast();

  const { preferences, blocked, isFullyBlocked } = useContactPreferences(leadId, {
    enabled: gate.canRead,
  });

  if (!gate.canRead) return null;

  return (
    <Block>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <BlockHeader>Restrições</BlockHeader>
        {gate.canWrite && (
          <button
            type="button"
            onClick={() => setBlockOpen(true)}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--brand-azul)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Gerenciar
          </button>
        )}
      </div>

      {blocked.length > 0 ? (
        <ContactRestrictionBanner blocked={blocked} isFullyBlocked={isFullyBlocked} />
      ) : (
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-3)',
            margin: 0,
            fontStyle: 'italic',
          }}
        >
          Nenhuma restrição de contato.
        </p>
      )}

      {blockOpen && (
        <BlockContactDialog
          leadId={leadId}
          {...(leadName ? { leadName } : {})}
          preferences={preferences}
          onClose={() => setBlockOpen(false)}
          onChanged={() => {
            setBlockOpen(false);
            toast('Preferência de contato atualizada.', 'success');
          }}
        />
      )}
    </Block>
  );
}

// ─── CommercialSection ────────────────────────────────────────────────────────

export interface CommercialSectionProps {
  conversationId: string;
  leadId: string | null;
  leadName: string | null;
}

/**
 * CommercialSection — seção "Comercial" do ContactPanel (F38-S04).
 *
 * Montada logo ABAIXO da `LeadSection`/`QualificationRuler` (a régua fica
 * acima — estado da IA; esta seção fica abaixo — estado do relacionamento
 * comercial, doc 28 §9). `leadId == null` (sem lead vinculado) ou os
 * gates de seção fechados → retorna `null`, nenhum nó extra no DOM.
 */
export function CommercialSection({
  conversationId,
  leadId,
  leadName,
}: CommercialSectionProps): React.JSX.Element | null {
  const { hasPermission, user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { enabled: sectionFlagEnabled, isLoading: sectionFlagLoading } =
    useFeatureFlag(SECTION_FLAG_KEY);
  const sectionCanRead = hasPermission(SECTION_READ_PERMISSION) && sectionFlagEnabled;

  const appointmentsGate = useAppointmentsGateLocal();
  const campaignGate = useCampaignMembershipGate();

  const { data: context, isLoading: contextLoading } = useCommercialContext(conversationId, {
    enabled: sectionCanRead && leadId !== null,
  });

  const { isFullyBlocked, blockedChannels } = useContactPreferences(leadId ?? '', {
    enabled: appointmentsGate.canRead && leadId !== null,
  });

  const [registerOpen, setRegisterOpen] = React.useState(false);

  function invalidateContext(): void {
    void queryClient.invalidateQueries({
      queryKey: conversationKeys.commercialContext(conversationId),
    });
  }

  // Sem lead vinculado, seção não existe (ação principal — "Registrar
  // contato" — não faz sentido sem lead) OU flag/permissão de composição
  // fechada (ainda carregando conta como fechada — evita "flash" de
  // conteúdo, mesmo padrão de QualificationRuler).
  if (leadId === null || sectionFlagLoading || !sectionCanRead) return null;

  const attemptsPreview = (context?.recentAttempts ?? [])
    .slice(0, 3)
    .map((attempt) => ({ id: attempt.id, ...describeAttempt(attempt) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 16px 0' }}>
        <BlockHeader>Comercial</BlockHeader>
      </div>

      {/* Ação principal da seção (§2.2.1 do plano): "Registrar contato" —
          reusa RegisterContactDialog, SEM onResolveQueueMember: o casamento
          com a campanha é server-side (F38-S03). */}
      {appointmentsGate.canWrite && (
        <div style={{ padding: '8px 16px 16px' }}>
          <Button
            variant="primary"
            size="sm"
            className="w-full justify-center"
            onClick={() => setRegisterOpen(true)}
            disabled={isFullyBlocked}
            title={
              isFullyBlocked ? 'Contato bloqueado — veja o aviso na seção Restrições' : undefined
            }
          >
            Registrar contato
          </Button>
        </div>
      )}

      <NextAppointmentBlock
        leadId={leadId}
        leadName={leadName}
        nextAppointment={context?.nextAppointment ?? null}
        isLoading={contextLoading}
        gate={appointmentsGate}
        isFullyBlocked={isFullyBlocked}
        onChanged={invalidateContext}
      />

      {campaignGate.canRead && (
        <div style={{ padding: '16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <CampaignMembershipCard
            campaign={context?.campaign ?? null}
            isLoading={contextLoading}
            currentUserId={user?.id ?? null}
            canOperate={campaignGate.canOperate}
          />
        </div>
      )}

      <AttemptsBlock leadId={leadId} gate={appointmentsGate} recentAttempts={attemptsPreview} />

      <RestrictionsBlock leadId={leadId} leadName={leadName} gate={appointmentsGate} />

      {registerOpen && (
        <RegisterContactDialog
          leadId={leadId}
          {...(leadName ? { leadName } : {})}
          blockedChannels={blockedChannels}
          origin="livechat"
          onClose={() => setRegisterOpen(false)}
          onRegistered={() => {
            setRegisterOpen(false);
            invalidateContext();
            toast('Contato registrado.', 'success');
          }}
        />
      )}
    </div>
  );
}
